import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Crown, KeyRound, Loader2, Save, Wand2 } from "lucide-react";
import { api, errMsg, fmtTs } from "@/lib/api";
import { PasswordField } from "@/components/PasswordField";

export default function Clients() {
  const [users, setUsers] = useState([]);
  const [plan, setPlan] = useState(null);
  const [reset, setReset] = useState(null); // {id,email} being reset
  const [pwd, setPwd] = useState("");
  const [busy, setBusy] = useState(false);
  const load = () => Promise.all([api.get("/admin/users"), api.get("/billing/status")]).then(([u, b]) => {
    setUsers(u.data);
    setPlan({ price_inr: b.data.price_inr, free_bot_limit: b.data.free_bot_limit, live_requires_pro: b.data.live_requires_pro, pro_label: b.data.pro_label });
  }).catch((e) => toast.error(errMsg(e)));
  useEffect(() => { load(); }, []);

  const togglePro = async (u) => {
    try { await api.put(`/admin/users/${u.id}/pro`, { published: !u.is_pro }); toast.success(`${u.email} → ${u.is_pro ? "Free" : plan.pro_label}`); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const savePlan = async () => {
    try { await api.put("/admin/plan", { ...plan, price_inr: Number(plan.price_inr), free_bot_limit: Number(plan.free_bot_limit) }); toast.success("Plan saved"); }
    catch (e) { toast.error(errMsg(e)); }
  };
  const genPwd = () => setPwd(Math.random().toString(36).slice(-4) + "@" + Math.random().toString(36).slice(-4).toUpperCase());
  const submitReset = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/admin/users/${reset.id}/reset-password`, { new_password: pwd });
      toast.success(`Temporary password set for ${reset.email}. Share it securely — they can change it after signing in.`);
      setReset(null); setPwd(""); load();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  return (
    <div className="space-y-3 rise" data-testid="clients-page">
      <h1 className="font-display text-xl font-bold text-white tracking-tight">Clients & Plan</h1>
      {plan && (
        <div className="panel p-4" data-testid="plan-editor">
          <div className="widget-title mb-3">Pricing plan (Razorpay)</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 items-end">
            <label className="flex flex-col gap-1"><span className="widget-title">Plan name</span><input data-testid="plan-label-input" className="field" value={plan.pro_label} onChange={(e) => setPlan({ ...plan, pro_label: e.target.value })} /></label>
            <label className="flex flex-col gap-1"><span className="widget-title">Price (₹, one-time)</span><input data-testid="plan-price-input" type="number" className="field" value={plan.price_inr} onChange={(e) => setPlan({ ...plan, price_inr: e.target.value })} /></label>
            <label className="flex flex-col gap-1"><span className="widget-title">Free paper bots</span><input data-testid="plan-free-bots-input" type="number" className="field" value={plan.free_bot_limit} onChange={(e) => setPlan({ ...plan, free_bot_limit: e.target.value })} /></label>
            <label className="flex items-center gap-2 h-8 text-xs text-slate-300 cursor-pointer">
              <input data-testid="plan-live-pro-checkbox" type="checkbox" checked={plan.live_requires_pro} onChange={(e) => setPlan({ ...plan, live_requires_pro: e.target.checked })} className="accent-[#22A34A]" /> Live execution needs paid plan
            </label>
            <button data-testid="plan-save-button" onClick={savePlan} className="btn-primary h-8"><Save className="w-3.5 h-3.5" /> Save plan</button>
          </div>
        </div>
      )}
      <div className="panel">
        <div className="px-3 py-2 border-b border-[#163056] widget-title">Registered users ({users.length})</div>
        <table className="tbl w-full" data-testid="clients-table">
          <thead><tr><th>Name</th><th>Email</th><th>Role</th><th>Plan</th><th>Broker</th><th>Paper bots</th><th>Live bots</th><th>Joined</th><th className="text-right">Action</th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} data-testid={`client-row-${u.id}`}>
                <td className="font-sans text-slate-100">{u.name}</td><td>{u.email}</td>
                <td className={u.role === "admin" ? "text-[#22A34A]" : "text-slate-400"}>{u.role.toUpperCase()}</td>
                <td className={u.is_pro ? "text-amber-300" : "text-slate-400"}>{u.is_pro ? plan?.pro_label || "Pro" : "Free"}</td>
                <td className={u.broker_connected ? "text-[#00E676]" : "text-slate-500"}>{u.broker_connected ? "Connected" : "—"}</td>
                <td>{u.paper_bots}</td><td className={u.live_bots ? "text-[#FF3B30]" : ""}>{u.live_bots}</td><td>{fmtTs(u.created_at)}</td>
                <td className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    {u.reset_requested_at && u.role !== "admin" && <span className="text-[9px] font-mono px-1.5 py-0.5 bg-amber-500/15 text-amber-300 border border-amber-500/30 rounded-sm" data-testid={`client-reset-requested-${u.id}`} title={`Reset requested ${fmtTs(u.reset_requested_at)}`}>RESET REQUESTED</span>}
                    {u.role !== "admin" && (
                      <button data-testid={`client-reset-pw-${u.id}`} onClick={() => { setReset({ id: u.id, email: u.email }); setPwd(""); }} className="btn-ghost h-6 px-2 text-xs"><KeyRound className="w-3 h-3" /> Reset PW</button>
                    )}
                    {u.role !== "admin" && (
                      <button data-testid={`client-pro-toggle-${u.id}`} onClick={() => togglePro(u)} className="btn-ghost h-6 px-2 text-xs">
                        <Crown className="w-3 h-3" /> {u.is_pro ? "Revoke" : "Grant"} {plan?.pro_label || "Pro"}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {reset && (
        <div className="fixed inset-0 z-50 bg-black/60 grid place-items-center p-4" data-testid="reset-pw-modal" onClick={() => setReset(null)}>
          <form onClick={(e) => e.stopPropagation()} onSubmit={submitReset} className="w-full max-w-sm panel p-6 rise">
            <h3 className="font-display text-lg font-bold text-white mb-1">Reset password</h3>
            <p className="text-xs text-slate-500 mb-4">Set a temporary password for <span className="text-slate-200 font-mono">{reset.email}</span>. Share it securely; they can change it after signing in.</p>
            <label className="flex flex-col gap-1"><span className="widget-title">Temporary password</span>
              <PasswordField testid="reset-pw-input" value={pwd} onChange={(e) => setPwd(e.target.value)} required minLength={6} autoComplete="new-password" /></label>
            <button type="button" onClick={genPwd} className="text-[11px] text-[#22A34A] hover:underline mt-2 inline-flex items-center gap-1" data-testid="reset-pw-generate"><Wand2 className="w-3 h-3" /> Generate strong password</button>
            <div className="flex gap-2 mt-5">
              <button type="button" onClick={() => setReset(null)} className="btn-ghost h-9 flex-1" data-testid="reset-pw-cancel">Cancel</button>
              <button type="submit" disabled={busy || pwd.length < 6} className="btn-primary h-9 flex-1" data-testid="reset-pw-submit">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Set password"}</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
